import { Loader2 } from 'lucide-react'
import { useUserAuthStore } from '../../store/userAuthStore'
import { useUserTrips } from '../../hooks/useUserTrips'
import { TripEditorCard } from '../../components/user/TripEditorCard'

export default function UserProfilePage() {
  const { user } = useUserAuthStore()
  const { trips, loading, error, updateTrip, deleteTrip } = useUserTrips({ limit: 5 })

  if (!user) {
    return (
      <div className="bg-white border border-gray-200 rounded-xl p-6 text-center text-sm text-gray-500">
        <Loader2 className="h-5 w-5 animate-spin mx-auto mb-2 text-gray-500" />
        Loading profile...
      </div>
    )
  }

  return (
    <div className="space-y-8">
      <section className="bg-white border border-gray-200 rounded-xl shadow-sm p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="h-20 w-20 rounded-full bg-gray-100 overflow-hidden">
              {user.avatar_url ? (
                <img
                  src={user.avatar_url}
                  alt={user.display_name}
                  className="h-full w-full object-cover"
                />
              ) : (
                <div className="h-full w-full flex items-center justify-center text-xl font-semibold text-gray-600">
                  {user.display_name.charAt(0).toUpperCase()}
                </div>
              )}
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">{user.display_name}</h2>
              <p className="text-sm text-gray-600">{user.email}</p>
              {user.username && (
                <p className="text-sm text-gray-500 mt-1">@{user.username}</p>
              )}
            </div>
          </div>

          <dl className="grid grid-cols-2 gap-4 text-sm text-gray-600">
            <div>
              <dt className="text-xs uppercase tracking-wide text-gray-500">Subscription</dt>
              <dd className="mt-1 font-medium text-gray-900">{user.subscription_type}</dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-wide text-gray-500">Verified</dt>
              <dd className="mt-1 font-medium text-gray-900">{user.is_verified ? 'Yes' : 'No'}</dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-wide text-gray-500">Provider</dt>
              <dd className="mt-1 font-medium text-gray-900">{user.auth_provider}</dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-wide text-gray-500">Created at</dt>
              <dd className="mt-1 font-medium text-gray-900">
                {new Date(user.created_at).toLocaleString()}
              </dd>
            </div>
          </dl>
        </div>

        {user.description && (
          <div className="mt-6">
            <h3 className="text-sm font-semibold text-gray-900">About</h3>
            <p className="mt-2 text-sm text-gray-600 whitespace-pre-wrap">
              {user.description}
            </p>
          </div>
        )}
      </section>

      <section className="bg-white border border-gray-200 rounded-xl shadow-sm p-6">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Your latest trips</h3>
            <p className="text-sm text-gray-600">
              Manage trip metadata directly against <code>api.waybook.me/trips</code>
            </p>
          </div>
        </div>

        <div className="mt-6 space-y-4">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-gray-500" />
            </div>
          ) : error ? (
            <div className="rounded-lg border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          ) : trips.length === 0 ? (
            <div className="rounded-lg border border-dashed border-gray-200 px-6 py-10 text-center text-sm text-gray-500">
              No trips found. Create your first trip from the "My Trips" tab.
            </div>
          ) : (
            trips.map((trip) => (
              <TripEditorCard
                key={trip.id}
                trip={trip}
                onSave={async (id, payload) => {
                  await updateTrip(id, {
                    title: payload.title ?? undefined,
                    description: payload.description ?? undefined,
                    status: payload.status,
                    privacy: payload.privacy,
                    type: payload.type,
                  })
                }}
                onDelete={deleteTrip}
              />
            ))
          )}
        </div>
      </section>
    </div>
  )
}
